import React, { useState } from 'react';
import dayjs from 'dayjs';
import { MdKeyboardArrowLeft } from "react-icons/md";
import { MdKeyboardArrowRight } from "react-icons/md";

const daysOfWeek = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const Daterange = () => {
  const [selectedDates, setSelectedDates] = useState([dayjs('2023-01-10'), dayjs('2023-01-28')]);
  const [month, setMonth] = useState(selectedDates[0].month()); // starting month from selection
  const [year, setYear] = useState(selectedDates[0].year());
  const [showCalendar, setShowCalendar] = useState(false);

  const getCalendarDays = () => {
    const start = dayjs(`${year}-${month + 1}-01`);
    const startWeekDay = (start.day() + 6) % 7; // Adjust for Mo-Su
    const days = [];

    let dateCursor = start.subtract(startWeekDay, 'day');
    for (let i = 0; i < 42; i++) {
      days.push(dateCursor);
      dateCursor = dateCursor.add(1, 'day');
    }

    return days;
  };

  const isInRange = (date) => {
    if (selectedDates.length < 2) return false;
    const [start, end] = selectedDates;
    return date.isAfter(start, 'day') && date.isBefore(end, 'day');
  };

  const isSelected = (date) => {
    return selectedDates.some(d => d.isSame(date, 'day'));
  };

  const handleClick = (date) => {
    if (selectedDates.length === 2) {
      setSelectedDates([date]);
    } else {
      const newDates = [...selectedDates, date].sort((a, b) => a.valueOf() - b.valueOf());
      setSelectedDates(newDates);
    }
  };

  const calendarDays = getCalendarDays();

  // Format display of selected dates
  const formatDisplay = () => {
    if (selectedDates.length === 1) {
      return selectedDates[0].format('MMM D, YYYY');
    } else if (selectedDates.length === 2) {
      return `${selectedDates[0].format('MMM D, YYYY')} - ${selectedDates[1].format('MMM D, YYYY')}`;
    }
    return 'Select date range';
  };

  return (
   <div className="relative inline-block w-[250px] font-sans">
      {/* Input/Button with arrow down */}
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full border rounded px-3 py-2 text-left flex justify-between items-center bg-white"
        type="button"
      >
        <span>{formatDisplay()}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform duration-200 ${showCalendar ? 'rotate-180' : 'rotate-0'}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Calendar popup */}
      {showCalendar && (
         <div className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg border p-4 w-[420px] flex text-sm">
          {/* Sidebar */}
          
          <div className="p-4 w-28 text-gray-700  border-r flex-col space-y-3">
            <div
              className="cursor-pointer hover:underline"
              onClick={() => {
                const today = dayjs();
                setSelectedDates([today, today]);
                setMonth(today.month());
                setYear(today.year());
                setShowCalendar(false);
              }}
            >
              Today
            </div>
            <div
              className="cursor-pointer hover:underline"
              onClick={() => {
                const yesterday = dayjs().subtract(1, 'day');
                setSelectedDates([yesterday, yesterday]);
                setMonth(yesterday.month());
                setYear(yesterday.year());
                setShowCalendar(false);
              }}
            >
              Yesterday
            </div>
            <div
              className="cursor-pointer hover:underline"
              onClick={() => {
                const lastWeekStart = dayjs().subtract(7, 'day');
                setSelectedDates([lastWeekStart, dayjs()]);
                setMonth(lastWeekStart.month());
                setYear(lastWeekStart.year());
                setShowCalendar(false);
              }}
            >
              Last week
            </div>
            <div
              className="cursor-pointer hover:underline"
              onClick={() => {
                const lastMonthStart = dayjs().subtract(1, 'month');
                setSelectedDates([lastMonthStart, dayjs()]);
                setMonth(lastMonthStart.month());
                setYear(lastMonthStart.year());
                setShowCalendar(false);
              }}
            >
              Last month
            </div>
            <div>
              <div
              className="text-blue-600  cursor-pointer hover:underline mt-28  "
              onClick={() => {
                setSelectedDates([]);
                setShowCalendar(false);
              }}
            >
              Reset
            </div>
              </div>
             
           
          </div>

          {/* Calendar */}
          <div className="p-4 flex-1">
            {/* Header */}
            <div className="flex items-center justify-between mb-2 ">
              
              <div className="font-semibold">
                {dayjs(new Date(year, month)).format('MMMM YYYY')}
              </div>

              <button
                className="text-black text-lg "
                onClick={() => {
                  if (month === 0) {
                    setMonth(11);
                    setYear(year - 1);
                  } else {
                    setMonth(month - 1);
                  }
                }}
              >
              <MdKeyboardArrowLeft />
              </button>
              <button
                className="text-black mr-24 text-lg"
                onClick={() => {
                  if (month === 11) {
                    setMonth(0);
                    setYear(year + 1);
                  } else {
                    setMonth(month + 1);
                  }
                }}
              >
               <MdKeyboardArrowRight />
              </button>
            </div>

            {/* Weekdays */}
            <div className="grid grid-cols-7 text-center mb-1 text-gray-500 ">
              {daysOfWeek.map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-7 text-center gap-y-1">
              {calendarDays.map((date, idx) => {
                const inCurrentMonth = date.month() === month;
                const isSelectedDate = isSelected(date);
                const inRange = isInRange(date);

                return (
                  <div
                    key={idx}
                    className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center
                      ${isSelectedDate ? 'bg-blue-600 text-white' : ''}
                      ${inRange ? 'bg-blue-50' : ''}
                      ${!inCurrentMonth ? 'text-gray-400' : 'text-black'}
                      hover:bg-blue-200 cursor-pointer`}
                    onClick={() => handleClick(date)}
                  >
                    {date.date()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Daterange;
